import './antDatePicker.less';

import React, { useMemo } from 'react';
import { DatePicker, DatePickerProps, TimePicker, TimePickerProps, TimeRangePickerProps } from 'antd';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import enGB from 'antd/locale/en_GB';
import nbNO from 'antd/locale/nb_NO';
import { ApplicationState } from 'framework/applicationState';
import { CssVariables } from 'styles/cssVariables';
import b_ from 'b_';

const datePickerB = b_.with('ant-date-picker');

// DatePicker/RangePicker/TimePicker each generate their own nominally-distinct `styles` type via
// antd's semantic-styles machinery, but they're structurally the same shape (a bag of optional
// CSSProperties, `popup` nested one level) modulo a couple of popup sub-keys DatePicker has and
// TimePicker doesn't. This local supertype covers every variant's usage in this file; assigning it
// into any one variant's narrower prop is safe since extra optional keys are simply ignored.
type PickerStyles = {
	root?: React.CSSProperties;
	prefix?: React.CSSProperties;
	input?: React.CSSProperties;
	suffix?: React.CSSProperties;
	popup?: {
		root?: React.CSSProperties;
		header?: React.CSSProperties;
		body?: React.CSSProperties;
		content?: React.CSSProperties;
		item?: React.CSSProperties;
		footer?: React.CSSProperties;
		container?: React.CSSProperties;
	};
};

function useDefaultPopupZIndex(styles: PickerStyles | undefined): PickerStyles | undefined {
	return useMemo(() => {
		if (styles?.popup?.root?.zIndex != null) {
			return styles;
		}
		return {
			...styles,
			popup: {
				...styles?.popup,
				root: {
					...styles?.popup?.root,
					zIndex: CssVariables.zIndexControlPopover,
				},
			},
		};
	}, [styles]);
}

type AntDatePickerProps = Omit<DatePickerProps, 'popupStyle' | 'styles'> & { styles?: PickerStyles };

export const AntDatePicker = observer((props: AntDatePickerProps) => {
	const className = classnames(datePickerB(), props.className);

	const { format, styles, ...others } = props;

	const stylesWrapped = useDefaultPopupZIndex(styles);
	const popupClasses = props.className
		?.split(' ')
		.map((c) => `${c}__popup`)
		.join(' ');

	return (
		<DatePicker
			format={format ?? ApplicationState.dateFormat}
			className={className}
			classNames={{ popup: { root: popupClasses } }}
			styles={stylesWrapped}
			locale={ApplicationState.locale == 'no' ? nbNO.DatePicker : enGB.DatePicker}
			{...others}
		/>
	);
});

export const AntDateTimePicker = observer((props: AntDatePickerProps) => {
	const { format, ...others } = props;

	return <DatePicker format={format ?? ApplicationState.dateTimeFormat} {...others} />;
});

const { RangePicker } = DatePicker;

type AntRangePickerProps = Omit<TimeRangePickerProps, 'popupStyle' | 'styles'> & { styles?: PickerStyles };

export const AntRangePicker = observer((props: AntRangePickerProps) => {
	const locale = ApplicationState.locale == 'no' ? nbNO.DatePicker : enGB.DatePicker;
	const { format, styles, ...others } = props;

	const stylesWrapped = useDefaultPopupZIndex(styles);

	return (
		<RangePicker format={format ?? ApplicationState.dateFormat} locale={locale} styles={stylesWrapped} {...others} />
	);
});

type AntTimePickerProps = Omit<TimePickerProps, 'popupStyle' | 'styles'> & { styles?: PickerStyles };

export const AntTimePicker = observer((props: AntTimePickerProps) => {
	const locale = ApplicationState.locale == 'no' ? nbNO.DatePicker : enGB.DatePicker;

	const { format, styles, ...others } = props;

	const stylesWrapped = useDefaultPopupZIndex(styles);

	return (
		<TimePicker format={format ?? ApplicationState.timeFormat} styles={stylesWrapped} locale={locale} {...others} />
	);
});
